import { useQuery } from '@tanstack/react-query'
import { resolvePlaceSearch } from 'tripPlanner/api'
import type { TripItineraryData } from 'tripPlanner/api/types'
import * as PlaceInfoKeys from 'tripPlanner/hooks/reactQueryKeys/placeInfo'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import HoverPreview from './PlaceHoverPreview/HoverPreview'
import type { LinkRect } from './PlaceHoverPreview/types'

const HOVER_PREVIEW_OFFER_TYPES = ['hotel', 'experience'] as const

export type HoverPreviewOfferType = typeof HOVER_PREVIEW_OFFER_TYPES[number]

export function isHoverPreviewOfferType(value: string | undefined): value is HoverPreviewOfferType {
  return HOVER_PREVIEW_OFFER_TYPES.includes(value as HoverPreviewOfferType)
}

export type ChatLinkHoverTarget =
  | {
    type: 'place'
    placeId: string
    placeName: string
    rect: LinkRect
  }
  | {
    type: 'placeSearch'
    searchQuery: string
    placeName: string
    rect: LinkRect
  }
  | {
    type: 'offer'
    offerId: string
    offerType: HoverPreviewOfferType
    offerName: string
    rect: LinkRect
  }

interface Props {
  target: ChatLinkHoverTarget
  isUser: boolean
  isDevMode: boolean
  onOfferClick: (data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => void
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  onClose: () => void
  onMouseEnter: () => void
  itineraryData: TripItineraryData | undefined
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}

function ChatLinkHoverPreview({
  target,
  isUser,
  isDevMode,
  onOfferClick,
  onPlaceClick,
  onClose,
  onMouseEnter,
  itineraryData,
  onSendMessage,
}: Props) {
  const placeSearchHoverQuery = target.type === 'placeSearch'
    ? target.searchQuery
    : undefined

  const { data: hoverResolvedPlace } = useQuery({
    queryKey: PlaceInfoKeys.resolveSearch(placeSearchHoverQuery ?? ''),
    queryFn: ({ signal }) => {
      if (!placeSearchHoverQuery) {
        throw new Error('Place search query is required')
      }
      return resolvePlaceSearch(placeSearchHoverQuery, undefined, signal)
    },
    enabled: !!placeSearchHoverQuery && !isUser && !isDevMode,
    staleTime: Infinity,
    retry: false,
  })

  if (isUser || isDevMode) return null

  if (target.type === 'offer') {
    return (
      <HoverPreview
        type="hotel"
        offerId={target.offerId}
        offerName={target.offerName}
        linkRect={target.rect}
        onClick={() => onOfferClick({
          offerId: target.offerId,
          offerType: target.offerType,
        })}
        onClose={onClose}
        onMouseEnter={onMouseEnter}
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
      />
    )
  }

  if (target.type === 'placeSearch') {
    if (!hoverResolvedPlace?.googlePlaceId) {
      return null
    }

    return (
      <HoverPreview
        type="place"
        placeId={hoverResolvedPlace.googlePlaceId}
        placeName={target.placeName}
        linkRect={target.rect}
        onClick={() => onPlaceClick({
          googlePlaceId: hoverResolvedPlace.googlePlaceId,
          placeName: target.placeName,
        })}
        onClose={onClose}
        onMouseEnter={onMouseEnter}
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
      />
    )
  }

  return (
    <HoverPreview
      type="place"
      placeId={target.placeId}
      placeName={target.placeName}
      linkRect={target.rect}
      onClick={() => onPlaceClick({
        googlePlaceId: target.placeId,
        placeName: target.placeName,
      })}
      onClose={onClose}
      onMouseEnter={onMouseEnter}
      itineraryData={itineraryData}
      onSendMessage={onSendMessage}
    />
  )
}

export default ChatLinkHoverPreview
